import laggy from 'api/middleware/laggy';
import {
  EntryWithHandle,
  fetchContentfulByHandle,
  fetchContentfulProductPage,
} from 'connectors/contentful';
import { useSegmentationContext } from 'contexts/segmentation';
import { useLocale } from 'hooks/use-locale';
import { useRegion } from 'hooks/use-region';
import { IProductPageFields, IProductPagesFields } from 'global';
import NotFound from 'pages/404';
import {
  Context as ContextType,
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useEffect,
} from 'react';
import { FormattedMessage } from 'react-intl';
import useSWR, { unstable_serialize } from 'swr';

interface ProviderProps<T = IProductPageFields | IProductPagesFields> {
  fallbackData: EntryWithHandle<T>;
  handle: string;
  isProductPage?: boolean;
  query?: any;
  /** Disable for ISR snapshots that should not be fetched again on hydration. */
  revalidate?: boolean;
}

export type DefaultContentfulType =
  EntryWithHandle<IProductPageFields> | EntryWithHandle<IProductPagesFields>;

interface MyContext<T = DefaultContentfulType> {
  data: T;
}

const ContentfulContext = createContext<MyContext | undefined>(undefined);

const ContentfulProvider = <
  T extends Record<string, any> = IProductPageFields | IProductPagesFields,
>({
  children,
  fallbackData,
  isProductPage = false,
  handle,
  query = null,
  revalidate = true,
}: PropsWithChildren<ProviderProps<T>>): ReactNode => {
  const locale = useLocale();
  const region = useRegion();
  const { pushSegmentation } = useSegmentationContext();
  // Pre-rendering with Default Data
  // See https://swr.vercel.app/docs/with-nextjs#pre-rendering-with-default-data

  const { error, data: fetchedData } = useSWR(
    handle && revalidate ? [handle, query, locale] : null,
    isProductPage
      ? () =>
          fetchContentfulProductPage({
            handle,
            locale,
            region,
          })
      : ([handle, q2]) => {
          return fetchContentfulByHandle({
            handle,
            locale,
            region,
            query: q2,
          });
        },
    {
      // https://github.com/vercel/swr/issues/1520#issuecomment-932339824
      fallback: {
        [unstable_serialize([handle, query, locale])]: fallbackData,
      },
      use: [laggy],
    }
  );

  const data = revalidate ? fetchedData : fallbackData;

  // If a segment is assigned to a page in Contentful, this will be pushed to the segmentation context
  useEffect(() => {
    // Check if fields exists and has the assignSegmentation property
    if (
      fallbackData?.fields &&
      'assignSegmentation' in fallbackData.fields &&
      fallbackData.fields.assignSegmentation
    ) {
      pushSegmentation({
        segmentation: fallbackData.fields.assignSegmentation,
      });
    }
  }, [fallbackData?.fields, pushSegmentation]);

  return (
    <ContentfulContext.Provider
      value={{
        data: data as DefaultContentfulType,
      }}
    >
      {!error && data === undefined ? (
        <div className="text-center">
          <FormattedMessage id="common:loading" />
        </div>
      ) : (
        <>{data ? children : <NotFound />}</>
      )}
    </ContentfulContext.Provider>
  );
};

function useContentful<T = DefaultContentfulType>(): MyContext<T> {
  const context = useContext<MyContext<T> | undefined>(
    ContentfulContext as unknown as ContextType<MyContext<T> | undefined>
  );

  if (!context) {
    throw new Error('useContentful must be used within a ContentfulProvider');
  }

  return context;
}

export { ContentfulProvider, useContentful };
